import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Phone, 
  Laptop, 
  HardDrive, 
  Cpu, 
  MemoryStick, 
  Monitor, 
  Wifi, 
  ShieldAlert,
  CheckCircle,
  Clock,
  ShieldCheck,
  ArrowRight,
  Star
} from "lucide-react";
import { QuoteForm } from "@/components/QuoteForm";
import { SEO } from "@/components/SEO";
import axios from "axios";
import { logger } from "@/lib/logger";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUSINESS_PHONE_LINK = "tel:+33549057490";

const repairServices = [
  {
    icon: ShieldAlert,
    title: "Nettoyage virus / malware",
    description: "Suppression complète des virus, malwares et logiciels espions.",
    price: "À partir de 69€",
    duration: "1-2h",
    popular: true
  },
  {
    icon: HardDrive,
    title: "Remplacement SSD/Disque dur",
    description: "Upgrade vers SSD pour des performances optimales.",
    price: "À partir de 109€",
    duration: "1-2h",
    popular: true
  },
  {
    icon: MemoryStick,
    title: "Ajout mémoire RAM",
    description: "Boostez les performances de votre ordinateur.",
    price: "À partir de 59€",
    duration: "30 min",
    popular: false
  },
  {
    icon: Monitor,
    title: "Réparation écran",
    description: "Remplacement d'écran cassé ou défectueux.",
    price: "À partir de 155€",
    duration: "1-2h",
    popular: false
  },
  {
    icon: Cpu,
    title: "Diagnostic complet",
    description: "Analyse complète de votre machine pour identifier les problèmes.",
    price: "Gratuit",
    duration: "30 min",
    popular: false
  },
  {
    icon: Wifi,
    title: "Problème réseau / WiFi",
    description: "Configuration et dépannage de votre connexion internet.",
    price: "À partir de 55€",
    duration: "Variable",
    popular: false
  }
];

const systemServices = [
  "Réinstallation Windows / macOS",
  "Récupération de données",
  "Configuration logiciels",
  "Sauvegarde automatique",
  "Mise à jour système",
  "Optimisation démarrage"
];

const advantages = [
  { icon: Clock, title: "Intervention rapide", description: "Dès 24-48h" },
  { icon: ShieldCheck, title: "Garantie 6 mois", description: "Pièces et main d'œuvre" },
  { icon: CheckCircle, title: "Toutes marques", description: "PC & Mac" },
  { icon: Star, title: "4,6/5 Google", description: "212 avis clients" }
];

export default function ComputerRepairPage() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API_URL}/services`);
        if (response.data.ordinateur) {
          setServices(response.data.ordinateur.services);
        }
      } catch (error) {
        logger.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  const computerRepairSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Réparation Ordinateur PC et Mac à Niort",
    serviceType: "Dépannage informatique",
    provider: {
      "@type": "LocalBusiness",
      name: "Mediastore Niort",
      telephone: "+33549057490",
      address: {
        "@type": "PostalAddress",
        streetAddress: "104 Rue de la Gare",
        addressLocality: "Niort",
        postalCode: "79000",
        addressCountry: "FR",
      },
    },
    areaServed: { "@type": "City", name: "Niort" },
    description:
      "Dépannage ordinateur PC et Mac à Niort. Spécialiste MacBook, iMac, Mac mini. Virus, lenteur, panne, écran cassé, remplacement SSD/RAM, récupération de données.",
  };

  return (
    <div className="min-h-screen" data-testid="computer-repair-page">
      <SEO
        title="Réparation Ordinateur PC & Mac à Niort - Dépannage Informatique Express"
        description="Spécialiste réparation Mac, MacBook, iMac et PC à Niort. Dépannage informatique : virus, lenteur, panne, écran, SSD, RAM, récupération de données. Diagnostic gratuit. Techniciens certifiés Apple."
        keywords="réparation ordinateur Niort, réparation PC Niort, réparation Mac Niort, réparation MacBook Niort, réparation iMac Niort, dépannage informatique Niort, dépannage PC Niort, virus ordinateur Niort, récupération données Niort, remplacement SSD Niort, ajout RAM Niort, réinstallation Windows Niort, spécialiste Apple Niort, informatique Niort"
        path="/reparation-ordinateur"
        schema={computerRepairSchema}
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Réparation Ordinateur", path: "/reparation-ordinateur" },
        ]}
      />
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-b from-emerald-50 to-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-emerald-100 text-emerald-700 px-4 py-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2 animate-pulse"></span>
                Dépannage informatique expert
              </Badge>
              
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900">
                Réparation <span className="text-emerald-600">Ordinateur</span> à Niort
              </h1>
              
              <p className="text-lg text-slate-600 leading-relaxed">
                Votre PC ou Mac est lent, infecté ou en panne ? Nos techniciens experts 
                diagnostiquent et réparent tous types d&apos;ordinateurs. <strong>Portable et fixe</strong>, 
                <strong> particuliers et professionnels</strong>.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href={BUSINESS_PHONE_LINK}>
                  <Button size="lg" className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 h-12 gap-2" data-testid="computer-hero-call-btn">
                    <Phone className="h-5 w-5" />
                    05 49 05 74 90
                  </Button>
                </a>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto h-12 gap-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                    Demander un devis
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1540589522742-82a91dac6fa0?w=800"
                alt="Réparation ordinateur Niort - Mediastore"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-4 hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Laptop className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">PC & Mac</p>
                    <p className="text-sm text-slate-500">Toutes marques</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-8 bg-white border-y">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {advantages.map((adv) => (
              <div key={adv.title} className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <adv.icon className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900 text-sm">{adv.title}</p>
                  <p className="text-xs text-slate-500">{adv.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Nos réparations ordinateur
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Dépannage complet pour tous vos problèmes informatiques
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {repairServices.map((service) => {
              const Icon = service.icon;
              return (
                <Card key={service.title} className="relative card-hover border-slate-200" data-testid={`computer-service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}>
                  {service.popular && (
                    <Badge className="absolute top-4 right-4 bg-emerald-600">Populaire</Badge>
                  )}
                  <CardContent className="p-6">
                    <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                      <Icon className="h-7 w-7 text-emerald-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 mb-4">
                      {service.description}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div>
                        <p className="font-semibold text-emerald-600">{service.price}</p>
                        <p className="text-xs text-slate-500">{service.duration}</p>
                      </div>
                      <Link to="/contact">
                        <Button size="sm" variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                          Devis gratuit
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* System Services */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Services logiciels
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                En plus des réparations matérielles, nous proposons des services de maintenance logicielle.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {systemServices.map((service) => (
                  <div key={service} className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-slate-700">{service}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Link to="/contact">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                    Demander un diagnostic
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Card className="p-6 text-center">
                <div className="text-4xl font-bold text-emerald-600 mb-2">Gratuit</div>
                <p className="text-slate-600">Diagnostic complet</p>
              </Card>
              <Card className="p-6 text-center">
                <div className="text-4xl font-bold text-emerald-600 mb-2">79€</div>
                <p className="text-slate-600">Réinstallation système</p>
              </Card>
              <Card className="p-6 text-center">
                <div className="text-4xl font-bold text-emerald-600 mb-2">79€</div>
                <p className="text-slate-600">Récupération données</p>
              </Card>
              <Card className="p-6 text-center">
                <div className="text-4xl font-bold text-emerald-600 mb-2">6</div>
                <p className="text-slate-600">Mois de garantie</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Ordinateur - SEO + UX */}
      <section className="section-padding bg-white" data-testid="computer-faq-section">
        <div className="container-custom max-w-4xl">
          <div className="text-center mb-12">
            <Badge className="bg-emerald-100 text-emerald-700 px-4 py-2 mb-4 hover:bg-emerald-100">FAQ</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Questions fréquentes - Réparation Ordinateur
            </h2>
            <p className="text-lg text-slate-600">
              Tout savoir sur le dépannage PC, Mac, MacBook et iMac à Niort
            </p>
          </div>

          <ol className="space-y-4 list-none p-0">
            {[
              { q: "Combien coûte un dépannage informatique à Niort ?", a: "Le diagnostic est entièrement gratuit chez Mediastore Niort. Les interventions démarrent à 55 € (réseau/WiFi) et 59 € (ajout de RAM). Le nettoyage virus est à partir de 69 €, le remplacement SSD à partir de 109 €. Main d'œuvre horaire : 79 €/h." },
              { q: "Combien de temps pour réparer un ordinateur ?", a: "La plupart des dépannages prennent entre 24 et 48 heures. Pour un changement de SSD ou un nettoyage virus, comptez 1 à 2 heures sur place. Les ordinateurs portables avec écran cassé peuvent prendre 48 à 72 heures (délai pièce)." },
              { q: "Réparez-vous tous les Mac (MacBook, iMac, Mac mini) ?", a: "Oui. Nos techniciens sont certifiés Apple et interviennent sur MacBook Pro, MacBook Air (Intel et Apple Silicon M1/M2/M3/M4), iMac 21\" et 27\", Mac mini et Mac Studio. Diagnostic gratuit. Main d'œuvre Mac : 79 €/h." },
              { q: "Mon PC est très lent, que faire ?", a: "Lenteur = signe d'un disque dur saturé, d'un virus, ou d'un manque de RAM. Nous diagnostiquons gratuitement la cause. Solution la plus efficace : passer du disque dur (HDD) au SSD pour multiplier la vitesse par 10. À partir de 109 €." },
              { q: "Pouvez-vous récupérer mes données ?", a: "Oui. Mediastore Niort propose un service de récupération de données sur disque dur défaillant, SSD, clé USB, carte SD. Tarif à partir de 95 €. Diagnostic gratuit avant intervention." },
              { q: "Travaillez-vous avec les ordinateurs PC fixes ?", a: "Absolument. Ordinateurs portables et fixes, toutes marques : Dell, HP, Lenovo, Asus, Acer, MSI, Microsoft Surface, custom builds. Réinstallation Windows 10/11 et macOS." },
              { q: "Garantie sur les réparations ordinateur ?", a: "Toutes nos interventions sont garanties 6 mois pièces et main d'œuvre. Pièces de qualité d'origine ou compatibles premium." },
            ].map((item, i) => (
              <li key={item.q} className="bg-slate-50 rounded-xl p-6 hover:bg-emerald-50 transition-colors" data-testid={`computer-faq-${i}`}>
                <h3 className="font-bold text-lg text-slate-900 mb-2 flex gap-3">
                  <span className="text-emerald-600 flex-shrink-0">{i + 1}.</span>
                  <span>{item.q}</span>
                </h3>
                <p className="text-slate-700 leading-relaxed pl-7">{item.a}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Tableau de prix PC + Mac */}
      <section className="section-padding bg-slate-50" data-testid="computer-pricing-section">
        <div className="container-custom max-w-5xl">
          <div className="text-center mb-10">
            <Badge className="bg-slate-900 text-white px-4 py-2 mb-4 hover:bg-slate-900">Tarifs</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Tarifs réparation ordinateur à Niort
            </h2>
            <p className="text-lg text-slate-600">PC, Mac, MacBook, iMac - garantie 6 mois</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
            <table className="w-full">
              <caption className="sr-only">Tarifs réparation et dépannage informatique</caption>
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th scope="col" className="text-left p-4 font-semibold">Intervention</th>
                  <th scope="col" className="text-center p-4 font-semibold">Tarif</th>
                  <th scope="col" className="text-center p-4 font-semibold">Durée</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Diagnostic complet PC/Mac", "Gratuit", "30 min"],
                  ["Nettoyage virus / malware", "À partir de 69 €", "1-2 h"],
                  ["Remplacement SSD (250 Go)", "À partir de 109 €", "1-2 h"],
                  ["Ajout mémoire RAM (8 Go)", "À partir de 59 €", "30 min"],
                  ["Réinstallation Windows / macOS", "À partir de 95 €", "2-3 h"],
                  ["Récupération de données", "À partir de 95 €", "Variable"],
                  ["Remplacement écran portable", "À partir de 155 €", "1-2 h"],
                  ["Réparation MacBook (M.O. 79 €/h)", "Sur devis", "3-5 jours"],
                ].map((row) => (
                  <tr key={row[0]} className="border-t border-slate-100 hover:bg-emerald-50 transition-colors">
                    <td className="p-4 font-medium text-slate-900">{row[0]}</td>
                    <td className="p-4 text-center text-emerald-600 font-semibold">{row[1]}</td>
                    <td className="p-4 text-center text-slate-700">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mt-8 text-slate-700">
            <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-emerald-600" /> Toutes marques PC (Dell, HP, Lenovo, Asus, MSI, Acer)</li>
            <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-emerald-600" /> Mac : MacBook Pro, Air, iMac, Mac mini</li>
            <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-emerald-600" /> Windows 10, Windows 11, macOS</li>
            <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-emerald-600" /> Particuliers et entreprises</li>
            <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-emerald-600" /> Devis transparent sous 24 h</li>
            <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-emerald-600" /> Garantie 6 mois pièces et main d&apos;œuvre</li>
          </ul>
        </div>
      </section>

      {/* Quote Form */}
      <section className="section-padding bg-white">
        <div className="container-custom max-w-4xl">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Demandez votre devis gratuit
            </h2>
            <p className="text-lg text-slate-600">
              Décrivez votre problème et recevez une estimation sous 24h
            </p>
          </div>
          <QuoteForm />
        </div>
      </section>
    </div>
  );
}
